import { useEffect, useState } from 'react';
import { ScrollView, Text } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import * as Location from 'expo-location';
import { useAuth } from '../context/AuthContext';
import { createShop, getShop, resolveMapsLink, updateShop } from '../api/shops';
import { Button, ErrorText, FormInput, Label, LoadingState } from '../components/ui';
import { colors } from '../theme';
import { ApiError } from '../api/client';
import { getCurrentPositionWithTimeout } from '../utils/location';

export default function CreateShopScreen() {
  const { token, user } = useAuth();
  const { shopId } = useLocalSearchParams<{ shopId?: string }>();
  const isEditing = !!shopId;
  const isIndividual = user?.posterKind === 'INDIVIDUAL';
  const router = useRouter();
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [description, setDescription] = useState('');
  const [locating, setLocating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(isEditing);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [mapsLink, setMapsLink] = useState('');
  const [resolvingLink, setResolvingLink] = useState(false);
  const [mapsLinkError, setMapsLinkError] = useState<string | null>(null);
  const [mapsLinkMessage, setMapsLinkMessage] = useState<string | null>(null);
  const [googleMapsUrl, setGoogleMapsUrl] = useState<string | undefined>(undefined);
  const [googleReviewLink, setGoogleReviewLink] = useState('');

  useEffect(() => {
    if (!token || !shopId) return;
    let cancelled = false;
    getShop(token, shopId)
      .then((shop) => {
        if (cancelled) return;
        setName(shop.name);
        setAddress(shop.address);
        setLatitude(String(shop.latitude));
        setLongitude(String(shop.longitude));
        setDescription(shop.description ?? '');
        setGoogleMapsUrl(shop.googleMapsUrl ?? undefined);
        setGoogleReviewLink(shop.googleReviewLink ?? '');
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e instanceof ApiError ? e.message : 'Không tải được thông tin shop');
      })
      .finally(() => {
        if (!cancelled) setLoadingExisting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, shopId]);

  const useMapsLink = async () => {
    // Both this and useCurrentLocation write latitude/longitude/googleMapsUrl;
    // without this lockout, firing both at once lets whichever resolves last
    // silently overwrite the other's result with no warning.
    if (!token || !mapsLink.trim() || locating) return;
    setMapsLinkError(null);
    setMapsLinkMessage(null);
    setResolvingLink(true);
    try {
      const result = await resolveMapsLink(token, mapsLink.trim());
      if (result.name) setName(result.name);
      if (result.address) setAddress(result.address);
      setGoogleMapsUrl(result.resolvedUrl);
      if (result.latitude != null && result.longitude != null) {
        setLatitude(String(result.latitude));
        setLongitude(String(result.longitude));
        setMapsLinkMessage('✓ Đã lấy đúng tên, địa chỉ và toạ độ từ Google Maps');
      } else {
        // Link only carried a name+address text, no coordinates — never guess
        // coordinates here, a wrong pin would wrongly fail a real check-in later.
        setMapsLinkMessage(
          '✓ Đã lấy tên và địa chỉ. Link này không kèm sẵn toạ độ — hãy đến đúng địa điểm rồi bấm "Lấy vị trí hiện tại" bên dưới để lấy toạ độ chính xác.',
        );
      }
    } catch (e) {
      setMapsLinkError(e instanceof ApiError ? e.message : 'Không đọc được link này, thử lại hoặc nhập tay bên dưới.');
    } finally {
      setResolvingLink(false);
    }
  };

  const useCurrentLocation = async () => {
    if (resolvingLink) return;
    setLocationError(null);
    setLocating(true);
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') {
        setLocationError(
          'Chưa được cấp quyền vị trí. Vào Cài đặt > Quyền riêng tư > Dịch vụ vị trí, bật quyền cho Expo Go (hoặc Loot nếu dùng bản cài đặt thật), rồi thử lại.',
        );
        return;
      }
      const pos = await getCurrentPositionWithTimeout({ accuracy: Location.Accuracy.High });
      setLatitude(String(pos.coords.latitude));
      setLongitude(String(pos.coords.longitude));
      // GPS coordinates no longer match whatever place the pasted link resolved to.
      setGoogleMapsUrl(undefined);
      // Best-effort: fill in a human-readable address too, so the user isn't
      // left staring at two raw numbers. Never let this fail the whole action —
      // the coordinates above are what actually matter for check-in.
      try {
        const [place] = await Location.reverseGeocodeAsync({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        });
        if (place) {
          const parts = [
            [place.streetNumber, place.street].filter(Boolean).join(' '),
            place.district,
            place.city ?? place.subregion,
          ].filter(Boolean);
          if (parts.length > 0) setAddress(parts.join(', '));
        }
      } catch {
        // No address text — the lat/lng fields are already filled, which is enough to submit.
      }
    } catch (e) {
      setLocationError(
        e instanceof Error
          ? `Lấy vị trí thất bại: ${e.message}`
          : 'Lấy vị trí thất bại — vui lòng kiểm tra Dịch vụ vị trí đã được bật hay chưa rồi thử lại.',
      );
    } finally {
      setLocating(false);
    }
  };

  const onSubmit = async () => {
    if (!token) return;
    setError(null);
    const lat = Number(latitude);
    const lng = Number(longitude);
    if (!name || !address || !latitude || !longitude || Number.isNaN(lat) || Number.isNaN(lng)) {
      setError('Vui lòng điền đầy đủ tên, địa chỉ và toạ độ — bấm "Lấy vị trí hiện tại" hoặc nhập trực tiếp');
      return;
    }
    if (name.trim().length < 2) {
      setError('Tên phải có ít nhất 2 ký tự');
      return;
    }
    if (address.trim().length < 5) {
      setError('Địa chỉ phải có ít nhất 5 ký tự');
      return;
    }
    setSaving(true);
    try {
      const input = {
        name,
        address,
        latitude: lat,
        longitude: lng,
        description: description || undefined,
        googleMapsUrl,
        googleReviewLink: googleReviewLink.trim() || undefined,
      };
      const shop = isEditing ? await updateShop(token, shopId!, input) : await createShop(token, input);
      router.replace(`/shop/${shop.id}`);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : isEditing
            ? 'Lưu thay đổi thất bại'
            : isIndividual
              ? 'Lưu địa điểm thất bại'
              : 'Tạo shop thất bại',
      );
    } finally {
      setSaving(false);
    }
  };

  if (loadingExisting) {
    return <LoadingState />;
  }

  return (
    <ScrollView contentContainerStyle={{ padding: 16 }} keyboardShouldPersistTaps="handled">
      <Label>Dán link Google Maps (tuỳ chọn)</Label>
      <FormInput
        value={mapsLink}
        onChangeText={setMapsLink}
        placeholder="https://maps.app.goo.gl/..."
        autoCapitalize="none"
      />
      <Button
        title={resolvingLink ? 'Đang đọc link...' : '📍 Lấy thông tin từ link'}
        variant="outline"
        onPress={useMapsLink}
        loading={resolvingLink}
        disabled={!mapsLink.trim() || locating}
      />
      <ErrorText>{mapsLinkError}</ErrorText>
      <Text style={{ color: mapsLinkMessage ? colors.success : colors.textMuted, marginTop: 4, marginBottom: 12, fontSize: 12 }}>
        {mapsLinkMessage ??
          'Mở Google Maps, tìm đúng shop, bấm "Chia sẻ" rồi dán liên kết vào đây — hệ thống sẽ tự điền tên và địa chỉ.'}
      </Text>

      <Label>{isIndividual ? 'Tên địa điểm' : 'Tên shop'}</Label>
      <FormInput
        value={name}
        onChangeText={setName}
        placeholder={isIndividual ? 'Nhà tôi, Văn phòng...' : 'Quán cà phê ABC'}
      />

      <Label>Địa chỉ</Label>
      <FormInput value={address} onChangeText={setAddress} placeholder="123 Nguyễn Huệ, Q1, TP.HCM" />

      <Label>Mô tả (tuỳ chọn)</Label>
      <FormInput
        value={description}
        onChangeText={setDescription}
        placeholder={isIndividual ? 'Ghi chú thêm (tầng, cổng, ...)' : 'Mô tả ngắn về shop'}
      />

      {!isIndividual ? (
        <>
          <Label>Link đánh giá nhanh của Google (tuỳ chọn)</Label>
          <FormInput
            value={googleReviewLink}
            onChangeText={setGoogleReviewLink}
            placeholder="https://g.page/r/..."
            autoCapitalize="none"
          />
          <Text style={{ color: colors.textMuted, marginTop: 4, marginBottom: 12, fontSize: 12 }}>
            Link này (miễn phí) giúp người nhận việc bấm 1 cái là vào thẳng ô viết đánh giá, thay vì phải tự
            tìm trong Google Maps. Lấy tại: Google Business Profile (business.google.com) → mục "Nhận thêm
            đánh giá" → sao chép link chia sẻ. Nếu chưa có, để trống — vẫn dùng được link Maps thường ở trên.
          </Text>
        </>
      ) : null}

      <Button
        title={locating ? 'Đang lấy vị trí...' : '📍 Lấy vị trí hiện tại'}
        variant="outline"
        onPress={useCurrentLocation}
        loading={locating}
        disabled={resolvingLink}
      />
      <ErrorText>{locationError}</ErrorText>

      <Text style={{ color: colors.textMuted, marginTop: 12, marginBottom: 4 }}>
        Hoặc nhập tay toạ độ (mở Google Maps, giữ ghim vị trí shop rồi sao chép toạ độ)
      </Text>
      <Label>Vĩ độ (latitude)</Label>
      <FormInput
        value={latitude}
        onChangeText={(v) => {
          setLatitude(v);
          setGoogleMapsUrl(undefined);
        }}
        keyboardType="numeric"
        placeholder="10.7769"
      />
      <Label>Kinh độ (longitude)</Label>
      <FormInput
        value={longitude}
        onChangeText={(v) => {
          setLongitude(v);
          setGoogleMapsUrl(undefined);
        }}
        keyboardType="numeric"
        placeholder="106.7009"
      />

      <ErrorText>{error || loadError}</ErrorText>

      <Button
        title={isEditing ? 'Lưu thay đổi' : isIndividual ? 'Lưu địa điểm' : 'Tạo shop'}
        onPress={onSubmit}
        loading={saving}
      />
    </ScrollView>
  );
}
